import React from 'react';
import { createRoot, Root } from 'react-dom/client';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { WidgetProvider, useWidgetContext } from './context/WidgetContext';
import { StripeProvider } from './components/StripeProvider';
import { InlinePaymentForm } from './components/InlinePaymentForm';
import type { WidgetConfig, Message, PaymentIntentResponse } from './types';
import './styles/widget.css';

// Configure marked for inline rendering (no wrapping <p> for short messages)
marked.setOptions({ breaks: true });

function renderMarkdown(content: string): string {
  const raw = marked.parse(content) as string;
  return DOMPurify.sanitize(raw);
}

// Main Widget Component
function ChatWidget(): JSX.Element {
  const {
    state,
    openWidget,
    closeWidget,
    toggleWidget,
    messages,
    isLoadingMessages,
    isSending,
    sendMessage,
    isRecording,
    isProcessingVoice,
    startRecording,
    stopRecording,
    cancelRecording,
    isVoiceSupported,
    error,
    resetError,
  } = useWidgetContext();

  const [inputValue, setInputValue] = React.useState('');
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when new messages arrive
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when widget opens
  React.useEffect(() => {
    if (state.isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [state.isOpen]);

  const handleSend = async () => {
    if (!inputValue.trim() || isSending) return;
    const message = inputValue;
    setInputValue('');
    await sendMessage(message);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleVoiceToggle = async () => {
    if (isRecording) {
      await stopRecording();
    } else {
      await startRecording();
    }
  };

  const position = state.config?.position || 'bottom-right';

  if (!state.isInitialized) {
    return <></>;
  }

  return (
    <div
      className={`cc-widget cc-widget--${position}`}
      style={{
        '--cc-primary': state.config?.primaryColor,
        '--cc-secondary': state.config?.secondaryColor,
      } as React.CSSProperties}
    >
      {/* Launcher Button */}
      <button
        className={`cc-launcher ${state.isOpen ? 'cc-launcher--open' : ''}`}
        onClick={toggleWidget}
        aria-label={state.isOpen ? 'Close chat' : 'Open chat'}
      >
        <svg className="cc-launcher__icon cc-launcher__icon--chat" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        <svg className="cc-launcher__icon cc-launcher__icon--close" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>

      {/* Chat Window */}
      <div className={`cc-window ${state.isOpen ? 'cc-window--open' : ''}`} role="dialog" aria-label="Chat">
        {/* Header */}
        <div className="cc-header">
          <div className="cc-header__logo">
            <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
            </svg>
          </div>
          <div className="cc-header__info">
            <div className="cc-header__title">{state.config?.businessName || 'Chat Support'}</div>
            <div className="cc-header__status">
              <span className="cc-header__status-dot" />
              Online
            </div>
          </div>
          <button className="cc-header__close" onClick={closeWidget} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Error Banner */}
        {error && (
          <div className="cc-error" role="alert">
            <svg className="cc-error__icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <span className="cc-error__message">{error}</span>
            <button className="cc-error__dismiss" onClick={resetError} aria-label="Dismiss error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Messages */}
        <div className="cc-messages" role="log" aria-live="polite">
          {messages.length === 0 && !isLoadingMessages ? (
            <div className="cc-empty">
              <svg className="cc-empty__icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
              </svg>
              <div className="cc-empty__title">{state.config?.greeting || 'How can we help?'}</div>
              <div className="cc-empty__subtitle">Send us a message to get started</div>
            </div>
          ) : (
            messages.map((message) => (
              <MessageBubble key={message.id} message={message} />
            ))
          )}

          {isSending && (
            <div className="cc-message cc-message--assistant">
              <div className="cc-message__avatar">
                <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
                </svg>
              </div>
              <div className="cc-typing">
                <span className="cc-typing__dot" />
                <span className="cc-typing__dot" />
                <span className="cc-typing__dot" />
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="cc-input-area">
          <div className="cc-input-wrapper">
            <textarea
              ref={inputRef}
              className="cc-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={state.config?.placeholder || 'Type a message...'}
              rows={1}
              disabled={isSending || isRecording}
            />
          </div>

          {/* Voice Button */}
          {state.config?.enableVoice && isVoiceSupported && (
            <button
              className={`cc-voice-btn ${isRecording ? 'cc-voice-btn--recording' : ''} ${isProcessingVoice ? 'cc-voice-btn--processing' : ''}`}
              onClick={handleVoiceToggle}
              disabled={isSending || isProcessingVoice}
              aria-label={isRecording ? 'Stop recording' : 'Start voice input'}
            >
              {isProcessingVoice ? (
                <span className="cc-spinner" />
              ) : isRecording ? (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <rect x="6" y="6" width="12" height="12" rx="2"/>
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm-1 1.93c-3.94-.49-7-3.85-7-7.93h2c0 3.03 2.47 5.5 5.5 5.5S17 11.03 17 8h2c0 4.08-3.06 7.44-7 7.93V19h3v2H9v-2h3v-3.07z"/>
                </svg>
              )}
            </button>
          )}

          {/* Send Button */}
          <button
            className="cc-send-btn"
            onClick={handleSend}
            disabled={!inputValue.trim() || isSending}
            aria-label="Send message"
          >
            {isSending ? (
              <span className="cc-spinner" />
            ) : (
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// Message Bubble Component
function MessageBubble({ message }: { message: Message }): JSX.Element {
  const isUser = message.role === 'user';

  return (
    <div className={`cc-message cc-message--${message.role}`}>
      {!isUser && (
        <div className="cc-message__avatar">
          <svg viewBox="0 0 24 24" fill="currentColor" width="16" height="16">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
          </svg>
        </div>
      )}
      <div className="cc-message__content">
        {isUser ? (
          message.content
        ) : (
          <div
            className="cc-markdown"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }}
          />
        )}
        {message.wasVoiceInput && (
          <div className="cc-message__voice-indicator">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
            </svg>
            Voice
          </div>
        )}

        {/* Render metadata cards */}
        {message.metadata && <MetadataCard metadata={message.metadata} />}
      </div>
    </div>
  );
}

// Metadata Card Component
function MetadataCard({ metadata }: { metadata: NonNullable<Message['metadata']> }): JSX.Element | null {
  const { acceptOffer, stripePublishableKey } = useWidgetContext();
  const [paymentIntent, setPaymentIntent] = React.useState<PaymentIntentResponse | null>(null);
  const [isAccepting, setIsAccepting] = React.useState(false);
  const [paymentSuccess, setPaymentSuccess] = React.useState(false);

  if (metadata.type === 'offer') {
    const handleAcceptOffer = async (paymentType: 'deposit' | 'full') => {
      setIsAccepting(true);
      const response = await acceptOffer(paymentType);
      setIsAccepting(false);
      if (response) {
        setPaymentIntent(response);
      }
    };

    // After payment success
    if (paymentSuccess) {
      return (
        <div className="cc-confirmation">
          <div className="cc-confirmation__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 6L9 17l-5-5"/>
            </svg>
          </div>
          <div className="cc-confirmation__title">Payment Successful!</div>
          <div className="cc-confirmation__order">
            {paymentIntent ? `Order #${paymentIntent.orderNumber}` : 'Processing...'}
          </div>
        </div>
      );
    }

    const priceFormatted = (metadata.priceCents / 100).toFixed(2);

    // Show inline card form after accepting offer
    if (paymentIntent && stripePublishableKey) {
      return (
        <div className="cc-offer">
          <div className="cc-offer__header">
            <span className="cc-offer__badge">Booking</span>
            <span className="cc-offer__product">{metadata.serviceName}</span>
          </div>
          <StripeProvider
            publishableKey={stripePublishableKey}
            clientSecret={paymentIntent.clientSecret}
          >
            <InlinePaymentForm
              amountCents={paymentIntent.amountCents}
              currency={paymentIntent.currency}
              customerEmail={metadata.customerEmail}
              onPaymentSuccess={() => setPaymentSuccess(true)}
            />
          </StripeProvider>
        </div>
      );
    }

    // Show offer with accept buttons
    return (
      <div className="cc-offer">
        <div className="cc-offer__header">
          <span className="cc-offer__badge">Booking</span>
          <span className="cc-offer__product">{metadata.serviceName}</span>
        </div>
        <div className="cc-offer__pricing">
          <span className="cc-offer__new-price">
            {metadata.currency} {priceFormatted}
          </span>
        </div>
        {metadata.offerMessage && (
          <div className="cc-offer__description">{metadata.offerMessage}</div>
        )}
        {metadata.highlights && metadata.highlights.length > 0 && (
          <ul className="cc-offer__highlights">
            {metadata.highlights.map((h, i) => (
              <li key={i}>{h}</li>
            ))}
          </ul>
        )}
        <div className="cc-offer__actions">
          <button
            className="cc-btn cc-btn--primary"
            onClick={() => handleAcceptOffer('full')}
            disabled={isAccepting}
          >
            {isAccepting ? <><span className="cc-spinner" /> Processing...</> : 'Pay Full Amount'}
          </button>
          <button
            className="cc-btn cc-btn--secondary"
            onClick={() => handleAcceptOffer('deposit')}
            disabled={isAccepting}
          >
            Pay Deposit
          </button>
        </div>
      </div>
    );
  }

  // Render inline payment form from message metadata (reload scenarios)
  if (metadata.type === 'payment_form') {
    if (paymentSuccess) {
      return (
        <div className="cc-confirmation">
          <div className="cc-confirmation__icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 6L9 17l-5-5"/>
            </svg>
          </div>
          <div className="cc-confirmation__title">Payment Successful!</div>
        </div>
      );
    }

    if (stripePublishableKey) {
      return (
        <StripeProvider
          publishableKey={stripePublishableKey}
          clientSecret={metadata.clientSecret}
        >
          <InlinePaymentForm
            amountCents={metadata.amountCents}
            currency={metadata.currency}
            onPaymentSuccess={() => setPaymentSuccess(true)}
          />
        </StripeProvider>
      );
    }

    return null;
  }

  if (metadata.type === 'payment_link') {
    return (
      <div className="cc-payment-link">
        <svg className="cc-payment-link__icon" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
        </svg>
        <div className="cc-payment-link__title">Payment Ready</div>
        <div className="cc-payment-link__subtitle">Click below to complete your purchase</div>
        <a href={metadata.paymentUrl} target="_blank" rel="noopener noreferrer" className="cc-btn cc-btn--success cc-btn--block">
          Complete Payment
        </a>
      </div>
    );
  }

  if (metadata.type === 'confirmation') {
    return (
      <div className="cc-confirmation">
        <div className="cc-confirmation__icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
        </div>
        <div className="cc-confirmation__title">Payment Successful!</div>
        <div className="cc-confirmation__order">Order #{metadata.orderNumber}</div>
        <div className="cc-confirmation__amount">
          {metadata.currency} {metadata.totalAmount.toFixed(2)}
        </div>
        {metadata.receiptUrl && (
          <a href={metadata.receiptUrl} target="_blank" rel="noopener noreferrer" className="cc-btn cc-btn--secondary cc-btn--sm" style={{ marginTop: '12px' }}>
            View Receipt
          </a>
        )}
      </div>
    );
  }

  return null;
}

// Global API interface
interface ChatCheckoutAPI {
  init: (config: WidgetConfig) => void;
  open: () => void;
  close: () => void;
  toggle: () => void;
  destroy: () => void;
}

// Mount function
let root: Root | null = null;
let widgetRef: {
  openWidget?: () => void;
  closeWidget?: () => void;
  toggleWidget?: () => void;
} = {};

function mountWidget(container: HTMLElement, config: WidgetConfig): void {
  root = createRoot(container);

  // Wrapper component to capture context methods
  function WidgetWrapper() {
    const context = useWidgetContext();

    React.useEffect(() => {
      context.initialize(config);
      widgetRef.openWidget = context.openWidget;
      widgetRef.closeWidget = context.closeWidget;
      widgetRef.toggleWidget = context.toggleWidget;
    }, []);

    return <ChatWidget />;
  }

  root.render(
    <WidgetProvider>
      <WidgetWrapper />
    </WidgetProvider>
  );
}

function unmountWidget(): void {
  if (root) {
    root.unmount();
    root = null;
  }
  widgetRef = {};
}

// Create global API
function createGlobalAPI(containerId: string): ChatCheckoutAPI {
  let container: HTMLElement | null = null;
  let currentConfig: WidgetConfig | null = null;

  return {
    init: (config: WidgetConfig) => {
      if (container) {
        console.warn('ChatCheckout widget is already initialized');
        return;
      }

      currentConfig = config;
      container = document.getElementById(containerId);

      if (!container) {
        container = document.createElement('div');
        container.id = containerId;
        document.body.appendChild(container);
      }

      mountWidget(container, config);
    },

    open: () => {
      widgetRef.openWidget?.();
    },

    close: () => {
      widgetRef.closeWidget?.();
    },

    toggle: () => {
      widgetRef.toggleWidget?.();
    },

    destroy: () => {
      unmountWidget();
      if (container && container.parentNode) {
        container.parentNode.removeChild(container);
      }
      container = null;
      currentConfig = null;
    },
  };
}

// Expose global API
const CONTAINER_ID = 'cc-widget-container';

declare global {
  interface Window {
    ChatCheckout: ChatCheckoutAPI;
  }
}

if (typeof window !== 'undefined') {
  window.ChatCheckout = createGlobalAPI(CONTAINER_ID);

  // Auto-initialize from script data attributes
  function autoInit(): void {
    // Find the script tag that loaded this widget
    const scripts = document.querySelectorAll('script');
    let widgetScript: HTMLScriptElement | null = null;

    scripts.forEach((script) => {
      if (script.src && script.src.includes('widget.js')) {
        widgetScript = script;
      }
    });

    // Also check for data-tenantSlug or data-tenant on any script
    if (!widgetScript) {
      scripts.forEach((script) => {
        if (script.dataset.tenantSlug || script.dataset.tenant) {
          widgetScript = script;
        }
      });
    }

    if (!widgetScript) {
      return; // No auto-init, user will call ChatCheckout.init() manually
    }

    const tenantSlug = widgetScript.dataset.tenantSlug || widgetScript.dataset.tenant;
    if (!tenantSlug) {
      return; // No tenant slug, can't auto-init
    }

    // Inject CSS if not already loaded
    const scriptSrc = widgetScript.src;
    const baseUrl = scriptSrc.substring(0, scriptSrc.lastIndexOf('/') + 1);
    const cssUrl = baseUrl + 'widget.css';

    if (!document.querySelector(`link[href="${cssUrl}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssUrl;
      document.head.appendChild(link);
    }

    const config: WidgetConfig = {
      tenantSlug,
      apiBaseUrl: widgetScript.dataset.apiUrl || widgetScript.dataset.apiBaseUrl,
      position: (widgetScript.dataset.position as 'bottom-right' | 'bottom-left') || 'bottom-right',
      primaryColor: widgetScript.dataset.primaryColor,
      secondaryColor: widgetScript.dataset.secondaryColor,
      greeting: widgetScript.dataset.greeting,
      placeholder: widgetScript.dataset.placeholder,
      autoOpen: widgetScript.dataset.autoOpen === 'true',
      enableVoice: widgetScript.dataset.enableVoice !== 'false',
    };

    window.ChatCheckout.init(config);
  }

  // Run auto-init when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }
}

// Export for ES modules
export { ChatWidget, WidgetProvider, useWidgetContext, createGlobalAPI };
export type { WidgetConfig, ChatCheckoutAPI };
